import React, {FC} from 'react';
import {connect} from 'react-redux';

import {useInitialEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {AppState} from '../../../../../store/appState';
import {fetchOrGetAccountAsyncAction} from '../../../../../store/data/accounts/accountsActions';
import {getAccounts} from '../../../../../store/data/dataSelectors';
import {getIsUserVisitor} from '../../../../../store/data/teammates/teammatesSelectors';
import {MessageViewerRecipient, MessageViewerRecipientProps} from './messageViewerRecipient';

/*
 * Props.
 */

interface MessageViewerRecipientContainerPublicProps extends MessageViewerRecipientProps {}
type MessageViewerRecipientContainerComponentProps = MessageViewerRecipientContainerPublicProps &
  ResolveDispatchProps<typeof mapDispatchToProps> &
  ResolveStateProps<typeof mapStateToProps>;

/*
 * Selectors.
 */

function getRecipientCard(_state: AppState, props: MessageViewerRecipientContainerPublicProps) {
  return props.recipient?.card;
}

function makeGetAccount() {
  return createSelector([getRecipientCard, getAccounts], (card, accounts) => {
    if (!card?.accountId) {
      return undefined;
    }
    return accounts[card.accountId];
  });
}

/*
 * Component.
 */

export const MessageViewerRecipientContainerComponent: FC<MessageViewerRecipientContainerComponentProps> = (
  props,
) => {
  const {
    panelId,
    searchQuery,
    recipient,
    isUnverified,
    message,
    hasMoreRecipients,
    isBold,
    needsParentHover,
    searchableAttributes,
    account,
    shouldHideClickableUntilHover,
    isUserVisitor,
    fetchOrGetAccountAsync,
  } = props;

  // If we have the new contact card, make sure we have an account loaded.
  // If the user is a visitor, we can not fetch anything.
  useInitialEffect(() => {
    if (recipient.card?.accountId && !isUserVisitor) {
      fetchOrGetAccountAsync(recipient.card.accountId);
    }
  });

  return (
    <MessageViewerRecipient
      panelId={panelId}
      searchQuery={searchQuery}
      recipient={recipient}
      isUnverified={isUnverified}
      message={message}
      hasMoreRecipients={hasMoreRecipients}
      isBold={isBold}
      needsParentHover={needsParentHover}
      searchableAttributes={searchableAttributes}
      account={account}
      shouldHideClickableUntilHover={shouldHideClickableUntilHover}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MessageViewerRecipientContainerPublicProps>()(() => ({
  account: makeGetAccount(),
  isUserVisitor: getIsUserVisitor,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  fetchOrGetAccountAsync: fetchOrGetAccountAsyncAction,
}));

export const MessageViewerRecipientContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MessageViewerRecipientContainerComponent);
